import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  model,
  signal,
} from '@angular/core';
import { FormatPipe, HoursPipe, UiCorePipesModule } from '@softline/ui-core';

import { DonutChartSegment } from './types/donut-chart-segment';

export type ChartType = 'donut' | 'gauge';

@Component({
  selector: 'soft-donut-chart',
  standalone: true,
  imports: [CommonModule, FormatPipe, UiCorePipesModule, HoursPipe],
  templateUrl: './donut-chart.component.html',
  styleUrl: './donut-chart.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DonutChartComponent {
  public readonly segments = input.required<DonutChartSegment[]>();
  public readonly size = input<string>('w-64 h-64');
  public readonly loading = input(false);
  public readonly title = input<string>('Doughnut Chart');
  public readonly numberSuffix = input<string>('');
  public readonly totalSuffix = input<string>('');
  public readonly numberFormat = input<string>('1.0-0');
  public readonly showTitle = input<boolean>(false);
  public readonly showTotal = input<boolean>(false);
  public readonly showPercentage = input<boolean>(false);
  public readonly percentageLabels = input<string[]>([]);
  public readonly withLegend = input<boolean>(false);
  public readonly numberType = input<'number' | 'hours'>('number');
  public readonly withAnimation = input<boolean>(true);
  public readonly customPercentage = input<number | null>(null);
  public readonly totalSize = input<string>('28px');
  public readonly legendPosition = input<'left' | 'right' | 'bottom'>('bottom');
  public readonly showTotalOfMostImportantSegment = input<boolean>(false);

  // Chart type input
  public readonly chartType = input<ChartType>('donut');

  // Gauge-specific inputs
  public readonly minValue = input<number>(0);
  public readonly maxValue = input<number>(100);
  public readonly startAngle = input<number>(-180);
  public readonly endAngle = input<number>(0);

  // Total & Percantage
  public readonly total = computed(() => {
    const data = this.segments();
    if (!data) return 0;
    return data.reduce((sum, d) => sum + Math.max(0, d.value), 0);
  });
  public readonly percentage = computed(() => {
    const total = this.total();
    const segments = this.segments().filter((s) =>
      this.percentageLabels().includes(s.label)
    );
    if (!total || total === 0 || segments.length === 0) return 0;
    const totalValue = segments.reduce((sum, segment) => sum + segment.value, 0);
    return this.customPercentage() !== null
      ? this.customPercentage()
      : (totalValue / total) * 100;
  });

  public mostImportantSegment = computed(() => {
    const data = this.segments();
    if (!data || data.length === 0)
      return {
        label: '',
        value: 0,
        percentage: 0,
      };

    const segment = data.reduce((prev, current) =>
      prev.value > current.value ? prev : current
    );
    const total = this.total();

    return {
      label: segment.label,
      value: segment.value,
      percentage: total > 0 ? (segment.value / total) * 100 : 0,
    };
  });

  // Legend
  public readonly highlightedSegment = model<string | null>(null);
  public readonly onSegmentClick = input<(label: string) => void>();

  // Animation
  private readonly animatedProgress = signal<number>(0);
  private readonly isAnimating = signal<boolean>(false);
  public readonly animationDelay = input<number>(300);
  public readonly animationDuration = input<number>(1500);

  // Support both donut and gauge
  public readonly animatedSvgSegments = computed(() => {
    const data = this.segments();
    const chartType = this.chartType();
    const radius = 50;
    const circumference = 2 * Math.PI * radius;
    const progress = this.withAnimation() ? this.animatedProgress() : 1;
    const total = this.total();
    if (!total || total === 0) return [];
    const validData = data.filter((d) => d.value > 0) ?? [];
    if (chartType === 'gauge') {
      return this.calculateGaugeSegments(
        validData,
        total,
        progress,
        radius,
        circumference
      );
    } else {
      return this.calculateDonutSegments(validData, total, progress, circumference);
    }
  });

  // Gauge needle angle calculation
  public readonly needleAngle = computed(() => {
    if (this.chartType() !== 'gauge') return 0;
    const currentValue = this.total();
    const min = this.minValue();
    const max = this.maxValue();
    const startAngle = this.startAngle();
    const endAngle = this.endAngle();
    const totalAngle = endAngle - startAngle;
    const normalizedValue = Math.max(0, Math.min(1, (currentValue - min) / (max - min)));
    return startAngle + totalAngle * normalizedValue;
  });

  // Gauge path calculation
  public readonly gaugePath = computed(() => {
    const startAngle = this.startAngle();
    const endAngle = this.endAngle();
    const radius = 50;
    const centerX = 60;
    const centerY = 60;
    const startX = centerX + radius * Math.cos((startAngle * Math.PI) / 180);
    const startY = centerY + radius * Math.sin((startAngle * Math.PI) / 180);
    const endX = centerX + radius * Math.cos((endAngle * Math.PI) / 180);
    const endY = centerY + radius * Math.sin((endAngle * Math.PI) / 180);
    const largeArcFlag = Math.abs(endAngle - startAngle) > 180 ? 1 : 0;
    return `M ${startX} ${startY} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endX} ${endY}`;
  });

  constructor() {
    effect(() => {
      const data = this.segments();
      const delay = this.animationDelay();
      const shouldAnimate = this.withAnimation();
      if (!data || data.length === 0) {
        this.animatedProgress.set(0);
        return;
      }
      if (!shouldAnimate) {
        this.animatedProgress.set(1);
        this.isAnimating.set(false);
        return;
      }
      this.animatedProgress.set(0);
      this.isAnimating.set(false);
      setTimeout(() => {
        this.isAnimating.set(true);
        this.animateProgress();
      }, delay);
    });
  }
  private animateProgress(): void {
    const duration = this.animationDuration();
    const startTime = performance.now();
    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = 1 - Math.pow(1 - progress, 3);
      this.animatedProgress.set(easedProgress);
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        this.isAnimating.set(false);
      }
    };
    requestAnimationFrame(animate);
  }

  private calculateDonutSegments(
    validData: DonutChartSegment[],
    total: number,
    progress: number,
    circumference: number
  ) {
    let cumulativeOffset = 0;
    return validData.map((d, i) => {
      const fraction = d.value / total;
      const fullLength = fraction * circumference;
      const animatedLength = fullLength * progress;
      const segment = {
        strokeDasharray: `${animatedLength} ${circumference}`,
        strokeDashoffset: -cumulativeOffset,
        color: d.color,
        label: d.label,
        value: d.value,
        id: `${d.label}_${i}_${d.value}`,
        path: null,
      };
      cumulativeOffset += animatedLength;
      return segment;
    });
  }

  private calculateGaugeSegments(
    validData: DonutChartSegment[],
    total: number,
    progress: number,
    radius: number,
    circumference: number
  ) {
    const startAngle = this.startAngle();
    const endAngle = this.endAngle();
    const totalAngle = endAngle - startAngle;
    let cumulativeAngle = startAngle;

    return validData.map((d, i) => {
      const fraction = d.value / total;
      const segmentAngle = totalAngle * fraction;
      const animatedAngle = segmentAngle * progress;
      const segmentLength = (animatedAngle / 360) * circumference;
      const offset = ((cumulativeAngle - startAngle) / 360) * circumference;
      const segment = {
        strokeDasharray: `${segmentLength} ${circumference}`,
        strokeDashoffset: -offset,
        color: d.color,
        label: d.label,
        value: d.value,
        id: `${d.label}_${i}_${d.value}`,
        path: this.gaugePath(),
      };
      cumulativeAngle += animatedAngle;
      return segment;
    });
  }
}
